import React, { useState } from "react";
import { useGetProjectDetails } from "../../../../../queries/query-hooks/workspace/workspace-hook";
import { useParams } from "react-router-dom";
import BasicColumnChart from "../../../../../components/chart/BasicColumnChart";
import { Col, Row } from "react-bootstrap";
import IndiaMap from "../../../../../components/chart/IndiaMap";
import DonutChart from "../../../../../components/chart/DonutChart";
import TreeMapChart from "../../../../../components/chart/TreeMapChart";
import PolarChart from "../../../../../components/chart/PolarChart";
import {
  useGetBrowserInfo,
  useGetContentReach,
  useGetContentView,
  useGetContentViewsAndReach,
  useGetDeviceInfo,
  useGetLocationInfo,
  useGetMobileDeviceInfo,
  useGetSpecialityInfo,
  useGetZoneInfo,
} from "../../../../../queries/query-hooks/workspace/sponsored-community-hook";
import {
  transformGraphData,
  transformHeatMapData,
} from "../../../../../common/utils/utils";
import {
  transformIndiaMapData,
  transformSpecialityData,
} from "../utils/commonFunctions";
import DashboardDropdown from "../../../../../components/DashboardDropdown";
import MixedLineChart from "../../../../../components/chart/MixedLineChart";
import SquareChartSkeleton from "../../../../../skeleton/workspace/clirnet/brand-ads/square-chart-skeleton";
import BarChartSkeleton from "../../../../../skeleton/workspace/clirnet/brand-ads/bar-chart-skeleton";
import Skeleton from "../../../../../components/skeleton";
import { ProjectHeaderTopBar, CardNotFound } from "@ui";

const Analytics = () => {
  const { project_id } = useParams();
  const [filterBy, setFilterBy] = useState("Views");
  const { data: projectDetails, isLoading: isProjectDetailsLoading } =
    useGetProjectDetails(project_id, true);
  const { data: contentView, isLoading: isContentViewLoading } =
    useGetContentView(
      project_id,
      {
        type: "all",
        content_type: "",
        content_id: "",
      },
      true
    );
  const { data: contentReach, isLoading: isContentReachLoading } =
    useGetContentReach(
      project_id,
      {
        type: "all",
        content_type: "",
        content_id: "",
      },
      true
    );
  const { data: contentCount, isLoading: isContentCountLoading } =
    useGetContentViewsAndReach(
      project_id,
      { type: filterBy?.toLowerCase() },
      true
    );
  const { data: specialityInfo, isLoading: isSpecialityInfoLoading } =
    useGetSpecialityInfo(
      project_id,
      {
        type: filterBy?.toLowerCase(),
        content_id: "",
        content_type: "",
        is_content: false,
      },
      true
    );

  const formattedSpecialityInfo = transformSpecialityData(specialityInfo);

  const contentViewChartOptions = {
    series: [
      {
        name: "View Count",
        data:
          contentView?.length > 0
            ? transformGraphData(contentView, "total_count", "formatted_date")
                ?.series
            : [],
        color: "#3256B8",
      },
    ],
    label:
      contentView?.length > 0
        ? transformGraphData(contentView, "total_count", "formatted_date")
            ?.label
        : [],
  };

  const contentReachChartOptions = {
    series: [
      {
        name: "Reach Count",
        data:
          contentReach?.length > 0
            ? transformGraphData(contentReach, "reach_count", "formatted_date")
                ?.series
            : [],
        color: "#159A6C",
      },
    ],
    label:
      contentReach?.length > 0
        ? transformGraphData(contentReach, "reach_count", "formatted_date")
            ?.label
        : [],
  };

  const { data: locationInfo, isLoading: isLocationInfoLoading } =
    useGetLocationInfo(
      project_id,
      {
        type: filterBy?.toLowerCase(),
        content_id: "",
        content_type: "",
        is_content: false,
      },
      true
    );
  const { data: zoneInfo, isLoading: isZoneInfoLoading } = useGetZoneInfo(
    project_id,
    {
      type: filterBy?.toLowerCase(),
      content_id: "",
      content_type: "",
      is_content: false,
    },
    true
  );

  const { data: deviceInfo, isLoading: isDeviceInfoLoading } = useGetDeviceInfo(
    project_id,
    {
      type: filterBy?.toLowerCase(),
      content_id: "",
      content_type: "",
      is_content: false,
    },
    true
  );

  const { data: mobileDeviceInfo, isLoading: isMobileDeviceInfoLoading } =
    useGetMobileDeviceInfo(
      project_id,
      {
        type: filterBy?.toLowerCase(),
        content_id: "",
        content_type: "",
        is_content: false,
      },
      true
    );

  const { data: browserInfo, isLoading: isBrowserInfoLoading } =
    useGetBrowserInfo(
      project_id,
      {
        type: filterBy?.toLowerCase(),
        content_id: "",
        content_type: "",
        is_content: false,
      },
      true
    );

  const indiaMapData = transformIndiaMapData(locationInfo);

  const zoneInfoChartOptions =
    zoneInfo && zoneInfo?.length > 0
      ? transformHeatMapData(
          zoneInfo,
          "name",
          filterBy === "Reads" ? "read_count" : "view_count"
        )
      : [];

  const deviceChartOptions = {
    series:
      deviceInfo?.length > 0
        ? transformGraphData(deviceInfo, "percentagebrowser_count", "Device")
            ?.series
        : [],
    label:
      deviceInfo?.length > 0
        ? transformGraphData(deviceInfo, "percentagebrowser_count", "Device")
            ?.label
        : [],
    title: "Device Distribution",
  };

  const mobileDeviceChartOptions = {
    series:
      mobileDeviceInfo?.length > 0
        ? transformGraphData(
            mobileDeviceInfo,
            "percentagebrowser_count",
            "Device"
          )?.series
        : [],
    label:
      mobileDeviceInfo?.length > 0
        ? transformGraphData(
            mobileDeviceInfo,
            "percentagebrowser_count",
            "Device"
          )?.label
        : [],
    title: "Mobile Device Distribution",
  };

  const browserChartOptions = {
    series:
      browserInfo?.length > 0
        ? transformGraphData(
            browserInfo,
            "percentagebrowser_count",
            "new_browser"
          )?.series
        : [],
    label:
      browserInfo?.length > 0
        ? transformGraphData(
            browserInfo,
            "percentagebrowser_count",
            "new_browser"
          )?.label
        : [],
  };

  const updateFilterHandler = (value) => {
    setFilterBy(value);
  };

  const showSpecialityInfo =
    projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "show_speciality_info"
      )?.attribute_value === "0"
      ? false
      : true;

  return (
    <div>
      <div className="mb-3">
        <ProjectHeaderTopBar
          projectType="CLIRNET Channel"
          title="Analytics"
          projectStartDate={projectDetails?.at(0)?.project_start_date || ""}
          projectEndDate={projectDetails?.at(0)?.project_end_date || ""}
          status={projectDetails?.at(0)?.project_status}
          isProjectDetailsLoading={isProjectDetailsLoading}
        />
      </div>

      <div className="mb-3 mb-md-4 row">
        <div className="col-md-6">
          {!isContentViewLoading && contentView?.length > 0 ? (
            <BasicColumnChart
              title={"Content Views"}
              series={contentViewChartOptions.series}
              labels={contentViewChartOptions.label}
            />
          ) : isContentViewLoading ? (
            <BarChartSkeleton />
          ) : (
            <CardNotFound title={"Content Views"} height={320} />
          )}
        </div>
        <div className="col-md-6">
          {!isContentReachLoading && contentReach?.length > 0 ? (
            <BasicColumnChart
              title={"Content Reach"}
              series={contentReachChartOptions.series}
              labels={contentReachChartOptions.label}
            />
          ) : isContentReachLoading ? (
            <BarChartSkeleton />
          ) : (
            <CardNotFound title={"Content Reach"} height={320} />
          )}
        </div>
      </div>

      <Row>
        <Col sm={12} className="mb-3">
          <div className="d-flex gap-3 align-items-center justify-content-end">
            <div className="fs-6 text-black">Filter By</div>
            <div className="dropdown bg-white border border-1 rounded-3 px-3 py-2">
              <DashboardDropdown
                constantFilterOption={["Views", "Reads"]}
                handleDropdownChange={updateFilterHandler}
                type={filterBy}
              />
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col
          md={12}
          lg={showSpecialityInfo ? 6 : 4}
          sm={12}
          className="d-flex flex-column pe-2 mb-4"
        >
          {!isLocationInfoLoading &&
          Object.keys(indiaMapData?.at(0)).length > 0 ? (
            <IndiaMap
              stateData={indiaMapData}
              title={`${filterBy} by Location`}
              showStateLegend={false}
              needDownload={true}
              valueSuffix={"%"}
            />
          ) : isLocationInfoLoading ? (
            <SquareChartSkeleton height={"100%"} />
          ) : (
            <CardNotFound title={`${filterBy} by Location`} />
          )}
        </Col>

        <Col md={12} lg={showSpecialityInfo ? 6 : 8} className="d-flex">
          <Row className="flex-grow-1">
            {showSpecialityInfo && (
              <Col md={12} sm={12} className="mb-4">
                {!isSpecialityInfoLoading && specialityInfo?.length > 0 ? (
                  <MixedLineChart
                    chartTitle={`${filterBy} by Speciality`}
                    series={formattedSpecialityInfo.series}
                    labels={formattedSpecialityInfo.labels}
                  />
                ) : isSpecialityInfoLoading ? (
                  <SquareChartSkeleton height={320} />
                ) : (
                  <CardNotFound
                    title={`${filterBy} by Speciality`}
                    height={320}
                    isTitleCenter={true}
                  />
                )}
              </Col>
            )}
            <Col md={12} sm={12} className="mb-4">
              {!isZoneInfoLoading && zoneInfo?.length > 0 ? (
                <TreeMapChart
                  title={`${filterBy} by Zone`}
                  series={zoneInfoChartOptions}
                />
              ) : isZoneInfoLoading ? (
                <BarChartSkeleton />
              ) : (
                <CardNotFound title={`${filterBy} by Zone`} height={320} />
              )}
            </Col>
          </Row>
        </Col>

        <Col sm={12} className="mb-4">
          {!isContentCountLoading ? (
            <div
              className="bg-primary-light border border-1 border-primary border-opacity-25 rounded-3 text-center text-primary mb-0 py-3 fs-5 poppins-medium"
              role="alert"
            >
              The community has received
              <span> {contentCount?.at(0)?.count} </span>
              {filterBy?.toLowerCase()} since last month
            </div>
          ) : isContentCountLoading ? (
            <Skeleton variant="text" width={"100%"} height={100} />
          ) : null}
        </Col>

        <Col md={4} sm={12} className="mb-4">
          {!isDeviceInfoLoading && deviceChartOptions?.series?.length > 0 ? (
            <DonutChart
              series={deviceChartOptions.series}
              labels={deviceChartOptions.label}
              title={`${filterBy} by Device`}
              graphColor={["#13388C", "#159A6C", "#CECDCD"]}
              height={320}
            />
          ) : isDeviceInfoLoading ? (
            <SquareChartSkeleton height={320} />
          ) : (
            <CardNotFound
              title={`${filterBy} by Device`}
              height={320}
              isTitleCenter={true}
            />
          )}
        </Col>

        <Col md={4} sm={12} className="mb-4">
          {!isMobileDeviceInfoLoading &&
          mobileDeviceChartOptions?.series?.length > 0 ? (
            <DonutChart
              series={mobileDeviceChartOptions.series}
              labels={mobileDeviceChartOptions.label}
              title={`${filterBy} by Mobile Device`}
              graphColor={["#13388C", "#159A6C", "#CECDCD"]}
              height={320}
            />
          ) : isMobileDeviceInfoLoading ? (
            <SquareChartSkeleton height={320} />
          ) : (
            <CardNotFound
              title={`${filterBy} by Mobile Device`}
              height={320}
              isTitleCenter={true}
            />
          )}
        </Col>

        <Col md={4} sm={12} className="mb-4">
          {!isBrowserInfoLoading && browserChartOptions?.series?.length > 0 ? (
            <PolarChart
              series={browserChartOptions.series}
              labels={browserChartOptions.label}
              title={`${filterBy} by Browser`}
              graphColor={[
                "#3256B8",
                "#159A6C",
                "#15607A",
                "#1D81A2",
                "#CECDCD",
              ]}
              height={320}
            />
          ) : isBrowserInfoLoading ? (
            <SquareChartSkeleton height={320} />
          ) : (
            <CardNotFound
              title={`${filterBy} by Browser`}
              height={320}
              isTitleCenter={true}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Analytics;
